import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { enqueueSnackbar } from "notistack";
import { IIncidentActivity } from "../interfaces/incident/IIncident";
import { showNotificationAlert } from "../../utils/notification/notificationHelper";

const findUnseenIds = (
  activities: IIncidentActivity[],
  seenIds: Set<number>,
): number[] => activities.map((a) => a.id).filter((id) => !seenIds.has(id));

/**
 * Watches an incident's activity list. The first list loaded is the baseline;
 * any activity that shows up after that triggers the alert + a message, and
 * its id is returned in `newActivityIds` so the timeline can mark it red.
 */
export const useNewActivityAlert = (
  incidentId: string | number | undefined,
  activities: IIncidentActivity[] | undefined,
): Set<number> => {
  const { t } = useTranslation();
  const seenIdsRef = useRef<Set<number> | null>(null);
  const [newActivityIds, setNewActivityIds] = useState<Set<number>>(new Set());

  // Another incident means a new baseline
  useEffect(() => {
    seenIdsRef.current = null;
    setNewActivityIds(new Set());
  }, [incidentId]);

  useEffect(() => {
    if (!activities) return;
    if (seenIdsRef.current === null) {
      seenIdsRef.current = new Set(activities.map((a) => a.id));
      return;
    }
    const unseenIds = findUnseenIds(activities, seenIdsRef.current);
    if (unseenIds.length === 0) return;

    unseenIds.forEach((id) => seenIdsRef.current!.add(id));
    setNewActivityIds((prev) => new Set(Array.from(prev).concat(unseenIds)));
    showNotificationAlert();
    enqueueSnackbar(t("incident.newActivityAlert"), { variant: "warning" });
  }, [activities, t]);

  return newActivityIds;
};
